/**
 * Custom Deck Creation — Multi-device mode.
 * ALL devices can enter cards for any available player.
 * Each device shows a list of players who haven't submitted yet,
 * user selects one, enters cards, submits.
 */

import * as ws from '../ws-client';
import { SyncedGameState } from '../../shared/protocol';
import { t } from '../i18n';

let localCards: { title: string; description: string; points: number }[] = [];
let selectedPlayerId: string | null = null;
let selectedPlayerName: string | null = null;

export function renderMultiDeckFromState(state: SyncedGameState): void {
  if (state.phase !== 'deck-creation') return;
  const dc = state.deckCreation;
  if (!dc) return;

  if (selectedPlayerId) {
    // Currently entering cards for a selected player
    const alreadySubmitted = dc.playersSubmitted.includes(selectedPlayerId);
    if (alreadySubmitted) {
      // Someone else submitted for this player while we were entering — go back to list
      selectedPlayerId = null;
      selectedPlayerName = null;
      localCards = [];
    } else {
      // DO NOT re-render the card entry form while actively typing.
      // Only re-render if we're on the player selection screen.
      return;
    }
  }

  // Show player selection + progress
  renderPlayerSelection(state);
}

function renderPlayerSelection(state: SyncedGameState): void {
  const app = document.getElementById('app')!;
  const dc = state.deckCreation!;
  const remaining = dc.playersRemaining || [];
  const submitted = dc.playersSubmitted || [];
  const claimed = dc.playersClaimed || [];

  app.innerHTML = `
    <div class="stack">
      <h1 style="font-size: var(--font-size-xl);">${t('customDeckProgress')}</h1>
      <p class="text-muted">${t('playersSubmitted', submitted.length, dc.totalPlayers)}</p>

      ${remaining.length > 0 ? `
        <div class="surface" style="padding: var(--space-md);">
          <p class="setting-label" style="margin-bottom: var(--space-sm);">${t('selectPlayerToEnter')}</p>
          <div class="stack-sm">
            ${remaining.map((p: any) => {
              const isClaimed = claimed.includes(p.id);
              return `
                <button class="btn btn-secondary" data-player-id="${p.id}" data-player-name="${p.name}"
                  style="text-align: left; justify-content: flex-start;${isClaimed ? ' opacity: 0.4; pointer-events: none;' : ''}"
                  ${isClaimed ? 'disabled' : ''}>
                  ${p.name}${isClaimed ? ' <span style="font-size: 0.75em; color: var(--color-warning);">(being entered...)</span>' : ''}
                </button>
              `;
            }).join('')}
          </div>
        </div>
      ` : `
        <div class="surface center" style="padding: var(--space-lg);">
          <p class="text-secondary">${t('allCardsSubmitted')}</p>
        </div>
      `}

      <div class="surface" style="padding: var(--space-md);">
        <p class="setting-label" style="margin-bottom: var(--space-xs);">${t('progress')}</p>
        <div class="stack-sm">
          ${state.players.map((p) => {
            const done = submitted.includes(p.id);
            return `
              <div class="row" style="justify-content: space-between; font-size: var(--font-size-sm);">
                <span>${done ? '\u2713' : '\u23F3'} ${p.displayName}</span>
                <span class="text-muted">${done ? `${dc.cardsPerPlayer} ${t('cards')}` : t('pending')}</span>
              </div>
            `;
          }).join('')}
        </div>
      </div>
    </div>
  `;

  // Bind player selection buttons
  document.querySelectorAll('[data-player-id]:not([disabled])').forEach((btn) => {
    btn.addEventListener('click', () => {
      selectedPlayerId = (btn as HTMLElement).dataset.playerId!;
      selectedPlayerName = (btn as HTMLElement).dataset.playerName!;
      localCards = [];
      // Claim this player so other devices see it as taken
      ws.claimPlayer(selectedPlayerId);
      renderCardEntry(state);
    });
  });
}

function renderCardEntry(state: SyncedGameState): void {
  const app = document.getElementById('app')!;
  const dc = state.deckCreation!;
  const needed = dc.cardsPerPlayer;
  const canSubmit = localCards.length >= needed;

  app.innerHTML = `
    <div class="stack">
      <div class="row" style="justify-content: space-between; align-items: center;">
        <h1 style="font-size: var(--font-size-xl);">${t('playerCards', selectedPlayerName || '')}</h1>
        <button class="btn btn-tertiary" id="btn-back-to-list" style="width: auto; padding: 4px 12px;">${t('back')}</button>
      </div>
      <p class="text-muted">${localCards.length}/${needed} ${t('cards')}</p>

      ${!canSubmit ? `
        <div class="surface" style="padding: var(--space-md);">
          <div class="stack-sm">
            <div>
              <label class="setting-label">${t('titleRequired')}</label>
              <input type="text" id="card-title" class="input" maxlength="80"
                placeholder="${t('titlePlaceholder')}" autofocus />
            </div>
            <div>
              <label class="setting-label">${t('descriptionOptional')}</label>
              <input type="text" id="card-desc" class="input" maxlength="200"
                placeholder="${t('descPlaceholder')}" />
            </div>
            <div>
              <label class="setting-label">${t('points')}</label>
              <div class="toggle-group">
                <button class="toggle-btn" data-pts="1">1</button>
                <button class="toggle-btn active" data-pts="2">2</button>
                <button class="toggle-btn" data-pts="3">3</button>
                <button class="toggle-btn" data-pts="4">4</button>
              </div>
            </div>
          </div>
        </div>
        <button class="btn btn-primary" id="btn-add-card">${t('addCard')}</button>
        <p id="card-error" class="error-text" style="min-height: 1.2em;"></p>
      ` : ''}

      ${localCards.length > 0 ? `
        <div class="surface" style="padding: var(--space-md);">
          <p class="setting-label" style="margin-bottom: var(--space-xs);">${t('cardsAdded')}</p>
          <div class="stack-sm">
            ${localCards.map((c, i) => `
              <div class="row" style="justify-content: space-between; font-size: var(--font-size-sm);">
                <span>${i + 1}. "${c.title}"</span>
                <span class="text-muted">${c.points}${t('pt')}</span>
              </div>
            `).join('')}
          </div>
        </div>
      ` : ''}

      ${canSubmit ? `
        <button class="btn btn-primary" id="btn-submit-cards" style="font-size: var(--font-size-lg);">
          ${t('doneSubmitAll')}
        </button>
      ` : ''}
    </div>
  `;

  // Back button
  document.getElementById('btn-back-to-list')?.addEventListener('click', () => {
    if (selectedPlayerId) {
      ws.unclaimPlayer(selectedPlayerId);
    }
    selectedPlayerId = null;
    selectedPlayerName = null;
    localCards = [];
    renderPlayerSelection(state);
  });

  // Points toggle
  let selectedPoints = 2;
  document.querySelectorAll('[data-pts]').forEach((btn) => {
    btn.addEventListener('click', () => {
      selectedPoints = parseInt((btn as HTMLElement).dataset.pts!);
      document.querySelectorAll('[data-pts]').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
    });
  });

  // Add card
  document.getElementById('btn-add-card')?.addEventListener('click', () => {
    const titleEl = document.getElementById('card-title') as HTMLInputElement;
    const descEl = document.getElementById('card-desc') as HTMLInputElement;
    const errorEl = document.getElementById('card-error')!;
    const title = titleEl.value.trim();
    if (!title) { errorEl.textContent = t('titleRequiredError'); return; }
    if (localCards.some((c) => c.title.toLowerCase() === title.toLowerCase())) {
      errorEl.textContent = t('cardAlreadyAdded'); return;
    }
    localCards.push({ title, description: descEl.value.trim() || title, points: selectedPoints });
    renderCardEntry(state);
  });

  // Submit
  document.getElementById('btn-submit-cards')?.addEventListener('click', () => {
    if (!selectedPlayerId) return;
    ws.submitCards(selectedPlayerId, localCards);
    // Reset and go back to list
    selectedPlayerId = null;
    selectedPlayerName = null;
    localCards = [];
    // State update will re-render via the gameplay router
  });
}

export function resetMultiDeckState(): void {
  localCards = [];
  selectedPlayerId = null;
  selectedPlayerName = null;
}
